import api from './api';
import publicApi from './publicApi';
import config from '../config/env.config';

export const menuService = {
  // Categories
  async getCategories(restaurantId = 1) {
    const response = await api.get(`/menu/categories?restaurant_id=${restaurantId}`);
    return response.data;
  },

  async createCategory(formData) {
    const response = await api.post('/menu/categories', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateCategory(id, formData) {
    const response = await api.put(`/menu/categories/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async deleteCategory(id) {
    const response = await api.delete(`/menu/categories/${id}`);
    return response.data;
  },

  async toggleCategoryStatus(id) {
    const response = await api.patch(`/menu/categories/${id}/toggle`);
    return response.data;
  },

  // Menu Items
  async getMenuItems(params = {}) {
    const queryParams = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    
    const response = await api.get(`/menu/items?${queryParams}`);
    return response.data;
  },

  async getMenuItemsCount(params = {}) {
    const queryParams = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    
    // This would need to be implemented in the backend
    const response = await api.get(`/menu/items/count?${queryParams}`);
    return response.data;
  },

  async getMenuItem(id) {
    const response = await api.get(`/menu/items/${id}`);
    return response.data;
  },

  async createMenuItem(formData) {
    const response = await api.post('/menu/items', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateMenuItem(id, formData) {
    const response = await api.put(`/menu/items/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async deleteMenuItem(id) {
    const response = await api.delete(`/menu/items/${id}`);
    return response.data;
  },

  async toggleMenuItemAvailability(id) {
    const response = await api.patch(`/menu/items/${id}/availability`);
    return response.data;
  },

  async deleteMenuItemImage(id) {
    const response = await api.delete(`/menu/items/image/${id}`);
    return response.data;
  },

  // Add-ons
  async getAddons() {
    const response = await api.get('/menu/addons');
    return response.data;
  },

  async createAddon(formData) {
    const response = await api.post('/menu/addons', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateAddon(id, formData) {
    const response = await api.put(`/menu/addons/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async deleteAddon(id) {
    const response = await api.delete(`/menu/addons/${id}`);
    return response.data;
  },

  async toggleAddonStatus(id) {
    const response = await api.patch(`/menu/addons/${id}/toggle`);
    return response.data;
  },

  // Menu Item Sizes
  async getMenuItemSizes(menuItemId) {
    const response = await api.get(`/menu/items/${menuItemId}/sizes`);
    return response.data;
  },

  async createMenuItemSize(menuItemId, formData) {
    const response = await api.post(`/menu/items/${menuItemId}/sizes`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateMenuItemSize(menuItemId, sizeId, formData) {
    const response = await api.put(`/menu/items/${menuItemId}/sizes/${sizeId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async deleteMenuItemSize(menuItemId, sizeId) {
    const response = await api.delete(`/menu/items/${menuItemId}/sizes/${sizeId}`);
    return response.data;
  },

  // Menu Item Add-ons Association
  async getMenuItemAddons(menuItemId) {
    const response = await api.get(`/menu/items/${menuItemId}/addons`);
    return response.data;
  },

  async addAddonToMenuItem(menuItemId, addonId, data = {}) {
    const response = await api.post(`/menu/items/${menuItemId}/addons/${addonId}`, data);
    return response.data;
  },

  async updateMenuItemAddon(menuItemId, addonId, data) {
    const response = await api.put(`/menu/items/${menuItemId}/addons/${addonId}`, data);
    return response.data;
  },

  async removeAddonFromMenuItem(menuItemId, addonId) {
    const response = await api.delete(`/menu/items/${menuItemId}/addons/${addonId}`);
    return response.data;
  },

  // Utility functions
  getImageUrl(imagePath) {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const base = config.API_URL;
    return `${base}/static/images/${imagePath}`;
  },

  // Bulk operations
  async bulkUpdateMenuItems(updates) {
    const response = await api.patch('/items/bulk', { updates });
    return response.data;
  },

  async bulkToggleAvailability(itemIds, isAvailable) {
    const response = await api.patch('/items/bulk-availability', {
      item_ids: itemIds,
      is_available: isAvailable
    });
    return response.data;
  },

  // Search and filtering
  async searchMenuItems(query, filters = {}) {
    const params = {
      search: query,
      ...filters
    };
    return this.getMenuItems(params);
  },

  async getMenuItemsByCategory(categoryId, params = {}) {
    return this.getMenuItems({
      category_id: categoryId,
      ...params
    });
  },

  // Statistics
  async getMenuStats() {
    const response = await api.get('/menu/stats');
    return response.data;
  },

  async getCategoryStats() {
    const response = await api.get('/menu/categories/stats');
    return response.data;
  },

  // Import/Export
  async exportMenu(format = 'json') {
    const response = await api.get(`/menu/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  async importMenu(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    Object.keys(options).forEach(key => {
      formData.append(key, options[key]);
    });

    const response = await api.post('/menu/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Public API methods
  async getPublicCategories() {
    const response = await publicApi.get('/public/categories');
    return response.data;
  },

  async getPublicMenuItems(outletId = null) {
    const params = outletId ? { outlet_id: outletId } : {};
    const response = await publicApi.get('/public/menu-items', { params });
    return response.data;
  }
};

export default menuService;